// SoftReservePage.tsx
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { wowheadItemUrl, refreshWowheadTooltips } from "./wowhead";

import { tc, useLegible } from "./theme/legible";
import { DISPLAY } from "./alignmentUi";
// ── Types ────────────────────────────────────────────────────

type LootItem = {
  id: number;
  external_item_id: string;
  name: string;
  quality: string | null;
  slot: string | null;
  boss_name: string | null;
  metadata: any;
  raid_option_id?: number;
  raid_label?: string;
};

type SoftReserve = {
  id: string;
  raid_id?: string;
  character_id: string;
  loot_item_id: number;
  note: string | null;
  created_at: string;
  updated_at: string;
  character_name: string;
  class_name: string | null;
  external_item_id: string;
  item_name: string;
  boss_name: string | null;
  item_metadata: any;
  raid_option_id?: number;
  raid_label?: string;
};

type HardReserve = {
  id: string;
  loot_item_id: number;
  reserved_for_character_id: string | null;
  note: string | null;
  created_at: string;
  reserved_for_name: string | null;
  external_item_id: string;
  item_name: string;
  boss_name: string | null;
  item_metadata: any;
};

type SRConfig = {
  id: string;
  raid_id: string;
  max_reserves_per_player: number;
  allow_duplicate_items: boolean;
  is_blind: boolean;
  locked_at: string | null;
  locked_by: string | null;
  effectivelyLocked: boolean;
  first_session: string | null;
};

type AuditEntry = {
  id: string;
  action: string;
  character_name: string | null;
  item_name: string | null;
  external_item_id: string | null;
  performed_by_name: string | null;
  details: any;
  created_at: string;
};

type RaidInfo = {
  id: string;
  name: string;
  guild_id: string;
  game_id: string;
  raid_option_id: number;
  raidName?: string;
  raidImageUrl?: string | null;
  hostedByGuild?: string;
  guildLogo?: string | null;
  sessions?: any[];
  anchorDate?: Date;
};

type MyCharacter = {
  id: string;
  name: string;
  class_name: string | null;
};

// ── WoW Class Colors ─────────────────────────────────────────

const CLASS_COLORS: Record<string, string> = {
  "Warrior": "#C69B6D",
  "Paladin": "#F48CBA",
  "Hunter": "#AAD372",
  "Rogue": "#FFF468",
  "Priest": "#FFFFFF",
  "Shaman": "#0070DD",
  "Mage": "#3FC7EB",
  "Warlock": "#8788EE",
  "Druid": "#FF7C0A",
  "Death Knight": "#C41E3A",
};

const QUALITY_COLORS: Record<string, string> = {
  "poor": "#9d9d9d",
  "common": "#ffffff",
  "uncommon": "#1eff00",
  "rare": "#0070dd",
  "epic": "#a335ee",
  "legendary": "#ff8000",
};

// ── Action Labels ────────────────────────────────────────────

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  "soft_add": { label: "Reserved", color: "text-emerald-700 dark:text-emerald-400" },
  "soft_remove": { label: "Unreserved", color: "text-red-600 dark:text-red-400" },
  "hard_add": { label: "Hard Reserved", color: "text-amber-700 dark:text-amber-400" },
  "hard_remove": { label: "HR Removed", color: "text-red-600 dark:text-red-400" },
  "lock": { label: "Locked", color: "text-red-600 dark:text-red-400" },
  "unlock": { label: "Unlocked", color: "text-emerald-700 dark:text-emerald-400" },
  "config_update": { label: "Config Changed", color: "text-cyan-700 dark:text-cyan-400" },
};

// ── Main Component ───────────────────────────────────────────

type SoftReservePageProps = {
  raidId: string;
  raidInfo: RaidInfo;
  canManage: boolean;
  myCharacters: MyCharacter[];
  enrolledCharacterId?: string;
  onBack: () => void;
};

export default function SoftReservePage({
  raidId,
  raidInfo,
  canManage,
  myCharacters,
  enrolledCharacterId,
  onBack,
}: SoftReservePageProps) {
  useLegible();
  // ── State ──────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [lootTable, setLootTable] = useState<LootItem[]>([]);
  const [softReserves, setSoftReserves] = useState<SoftReserve[]>([]);
  const [hardReserves, setHardReserves] = useState<HardReserve[]>([]);
  const [config, setConfig] = useState<SRConfig | null>(null);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);

  // UI State
  const [activeTab, setActiveTab] = useState<"items" | "reserves" | "hard" | "audit">("items");
  const [searchQuery, setSearchQuery] = useState("");
  const [bossFilter, setBossFilter] = useState<string | null>(null);
  const [selectedCharId, setSelectedCharId] = useState<string>(
    enrolledCharacterId && myCharacters.some(c => c.id === enrolledCharacterId)
      ? enrolledCharacterId
      : myCharacters[0]?.id || ""
  );
  const [gargulString, setGargulString] = useState<string | null>(null);
  const [gargulCopied, setGargulCopied] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [showSrComplete, setShowSrComplete] = useState(false);
  
  // Chain raid tab state: null = "All", or a specific raid_label
  const [activeRaidTab, setActiveRaidTab] = useState<string | null>(null);

  // Auto-dismiss SR complete celebration after 8 seconds
  useEffect(() => {
    if (showSrComplete) {
      const timer = setTimeout(() => setShowSrComplete(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [showSrComplete]);

  // Config edit state (officer only)
  const [editMaxReserves, setEditMaxReserves] = useState(2);
  const [editAllowDupes, setEditAllowDupes] = useState(true);
  const [editBlind, setEditBlind] = useState(false);

  // ── Data Fetching ──────────────────────────────────────────

  const fetchReserves = useCallback(async () => {
    try {
      const res = await fetch(`/raids/${encodeURIComponent(raidId)}/reserves`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load reserves");
      const data = await res.json();
      setSoftReserves(data.softReserves || []);
      setHardReserves(data.hardReserves || []);
      setConfig(data.config || null);
      if (data.config) {
        setEditMaxReserves(data.config.max_reserves_per_player);
        setEditAllowDupes(data.config.allow_duplicate_items);
        setEditBlind(data.config.is_blind || false);
      }
    } catch (e: any) {
      setError(e.message);
    }
  }, [raidId]);

  const fetchLootTable = useCallback(async () => {
    try {
      const res = await fetch(`/raids/${encodeURIComponent(raidId)}/loot-table`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load loot table");
      const data = await res.json();
      setLootTable(data);
    } catch (e: any) {
      setError(e.message);
    }
  }, [raidId]);

  const fetchAudit = useCallback(async () => {
    if (!canManage) return;
    try {
      const res = await fetch(`/raids/${encodeURIComponent(raidId)}/reserves/audit`, { credentials: "include" });
      if (res.ok) setAuditLog(await res.json());
    } catch (_) {}
  }, [raidId, canManage]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchLootTable(), fetchReserves(), fetchAudit()])
      .finally(() => setLoading(false));
  }, [fetchLootTable, fetchReserves, fetchAudit]);

  // Refresh Wowhead tooltips when data changes
  useEffect(() => {
    const timer = setTimeout(refreshWowheadTooltips, 200);
    return () => clearTimeout(timer);
  }, [lootTable, softReserves, hardReserves, activeTab, searchQuery, bossFilter]);

  // ── Derived Data ───────────────────────────────────────────

  // Detect if this is a chained raid (multiple raid_labels in loot table)
  const chainRaidLabels = useMemo(() => {
    const labels = new Set(lootTable.map(i => i.raid_label).filter(Boolean) as string[]);
    return Array.from(labels);
  }, [lootTable]);
  const isChainedRaid = chainRaidLabels.length > 1;

  const bossList = useMemo(() => {
    let items = lootTable;
    // If chain tab active, only show bosses from that raid
    if (isChainedRaid && activeRaidTab) {
      items = items.filter(i => i.raid_label === activeRaidTab);
    }
    const bosses = new Set(items.map(i => i.boss_name).filter(Boolean) as string[]);
    return Array.from(bosses).sort();
  }, [lootTable, isChainedRaid, activeRaidTab]);

  const filteredLoot = useMemo(() => {
    let items = lootTable;
    // Filter by chain raid tab (but search still works across all)
    if (isChainedRaid && activeRaidTab && !searchQuery.trim()) {
      items = items.filter(i => i.raid_label === activeRaidTab);
    }
    if (bossFilter) items = items.filter(i => i.boss_name === bossFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      // Search across ALL raids in chain regardless of active tab
      items = lootTable.filter(i => i.name.toLowerCase().includes(q) || (i.boss_name || "").toLowerCase().includes(q));
      if (bossFilter) items = items.filter(i => i.boss_name === bossFilter);
    }
    return items;
  }, [lootTable, bossFilter, searchQuery, isChainedRaid, activeRaidTab]);

  const myReserves = useMemo(() => 
    softReserves.filter(sr => sr.character_id === selectedCharId),
  [softReserves, selectedCharId]);

  const hardReservedItemIds = useMemo(() => 
    new Set(hardReserves.map(hr => hr.loot_item_id)),
  [hardReserves]);

  const myReservedItemIds = useMemo(() => 
    new Set(myReserves.map(sr => sr.loot_item_id)),
  [myReserves]);

  // Group reserves by player for the "Reserves" tab view
  const reservesByPlayer = useMemo(() => {
    const map = new Map<string, { characterId: string; characterName: string; className: string | null; reserves: SoftReserve[] }>();
    for (const sr of softReserves) {
      if (!map.has(sr.character_id)) {
        map.set(sr.character_id, {
          characterId: sr.character_id,
          characterName: sr.character_name,
          className: sr.class_name,
          reserves: [],
        });
      }
      map.get(sr.character_id)!.reserves.push(sr);
    }
    return Array.from(map.values()).sort((a, b) => a.characterName.localeCompare(b.characterName));
  }, [softReserves]);

  const isLocked = config?.effectivelyLocked || false;

  // ── Actions ────────────────────────────────────────────────

  function showToast(message: string, type: "success" | "error" = "success") {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }

  // Player-level note (once-off, not per item)
  const [playerNote, setPlayerNote] = useState("");
  const [showNoteEditor, setShowNoteEditor] = useState(false);

  async function handleAddReserve(lootItemId: number) {
    if (!selectedCharId) return showToast("Select a character first", "error");
    if (isLocked) return showToast("Reserves are locked", "error");
    setActionLoading(`add-${lootItemId}`);
    try {
      const res = await fetch(`/raids/${encodeURIComponent(raidId)}/soft-reserves`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ characterId: selectedCharId, lootItemId, note: null }),
      });
      if (!res.ok) {
        const data = await res.json();
        const msg = data.error === "max_reserves_reached" ? `Max ${data.max} reserves reached`
          : data.error === "already_reserved" ? "Already reserved this item"
          : data.error === "item_hard_reserved" ? "This item is hard reserved"
          : data.error || "Failed to reserve";
        return showToast(msg, "error");
      }
      showToast("Item reserved!");
      await fetchReserves();
      // Check if user just hit max reserves — trigger celebration
      // We need to re-derive myReserves count after fetch completes
      // fetchReserves updates softReserves state, but since we can't read the new state immediately,
      // we check optimistically: current count + 1
      const currentCount = myReserves.length;
      const maxReserves = config?.max_reserves_per_player || 2;
      if (currentCount + 1 >= maxReserves) {
        setShowSrComplete(true);
      }
    } catch (e: any) {
      showToast(e.message, "error");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleRemoveReserve(reserveId: string) {
    if (isLocked) return showToast("Reserves are locked", "error");
    setActionLoading(`remove-${reserveId}`);
    try {
      const res = await fetch(`/raids/${encodeURIComponent(raidId)}/soft-reserves/${reserveId}`, {
        method: "DELETE", credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json();
        return showToast(data.error || "Failed to remove reserve", "error");
      }
      showToast("Reserve removed");
      await fetchReserves();
    } catch (e: any) {
      showToast(e.message, "error");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleAddHardReserve(lootItemId: number, note?: string) {
    setActionLoading(`hr-add-${lootItemId}`);
    try {
      const res = await fetch(`/raids/${encodeURIComponent(raidId)}/hard-reserves`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lootItemId, note: note || null }),
      });
      if (!res.ok) {
        const data = await res.json();
        return showToast(data.error || "Failed", "error");
      }
      showToast("Hard reserve set");
      await fetchReserves();
    } catch (e: any) {
      showToast(e.message, "error");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleRemoveHardReserve(reserveId: string) {
    setActionLoading(`hr-remove-${reserveId}`);
    try {
      const res = await fetch(`/raids/${encodeURIComponent(raidId)}/hard-reserves/${reserveId}`, {
        method: "DELETE", credentials: "include",
      });
      if (!res.ok) return showToast("Failed to remove", "error");
      showToast("Hard reserve removed");
      await fetchReserves();
    } catch (e: any) {
      showToast(e.message, "error");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleToggleLock() {
    const newLock = !isLocked;
    setActionLoading("lock");
    try {
      const res = await fetch(`/raids/${encodeURIComponent(raidId)}/soft-reserve-config/lock`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lock: newLock }),
      });
      if (!res.ok) return showToast("Failed to toggle lock", "error");
      showToast(newLock ? "Reserves locked" : "Reserves unlocked");
      await fetchReserves();
      if (canManage) fetchAudit();
    } catch (e: any) {
      showToast(e.message, "error");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleSaveConfig() {
    setActionLoading("config");
    try {
      const res = await fetch(`/raids/${encodeURIComponent(raidId)}/soft-reserve-config`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ maxReservesPerPlayer: editMaxReserves, allowDuplicateItems: editAllowDupes, isBlind: editBlind }),
      });
      if (!res.ok) return showToast("Failed to update config", "error");
      showToast("Settings saved");
      setShowConfigPanel(false);
      await fetchReserves();
    } catch (e: any) {
      showToast(e.message, "error");
    } finally {
      setActionLoading(null);
    }
  }

  async function handleExportGargul() {
    setActionLoading("gargul");
    try {
      const res = await fetch(`/raids/${encodeURIComponent(raidId)}/reserves/export/gargul`, { credentials: "include" });
      if (!res.ok) return showToast("Export failed", "error");
      const data = await res.json();
      setGargulString(data.gargulString);
    } catch (e: any) {
      showToast(e.message, "error");
    } finally {
      setActionLoading(null);
    }
  }

  function copyGargul() {
    if (!gargulString) return;
    navigator.clipboard.writeText(gargulString);
    setGargulCopied(true);
    setTimeout(() => setGargulCopied(false), 2000);
  }

  // ── Loading State ──────────────────────────────────────────

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto py-12 px-4">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-[var(--c-ink-3)] hover:text-[var(--c-ink)] transition-colors text-sm">← Back</button>
        </div>
        <div className="mt-8 flex items-center justify-center py-20">
          <div className="text-[var(--c-ink-3)] animate-pulse">Loading soft reserves...</div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="max-w-5xl mx-auto py-12 px-4">
        <div className="flex items-center gap-3 mb-8">
          <button onClick={onBack} className="text-[var(--c-ink-3)] hover:text-[var(--c-ink)] transition-colors text-sm">← Back</button>
        </div>
        <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] p-12 text-center">
          <div className="text-4xl mb-4">📦</div>
          <div className="text-[var(--c-ink-2)] text-sm">Soft reserves are not enabled for this raid.</div>
          {canManage && (
            <button
              onClick={async () => {
                await fetch(`/raids/${encodeURIComponent(raidId)}/soft-reserve-config`, {
                  method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ maxReservesPerPlayer: 2, allowDuplicateItems: true }),
                });
                await fetchReserves();
              }}
              className="mt-4 px-6 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-bold rounded-lg transition-colors"
            >
              Enable Soft Reserves
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────

  return (
    <div className="max-w-6xl mx-auto py-6 px-4 space-y-6 animate-in fade-in duration-300">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg text-sm font-bold shadow-xl border ${
          toast.type === "success"
            ? "bg-emerald-500/10 dark:bg-emerald-900/90 border-emerald-500/30 text-emerald-800 dark:text-emerald-200"
            : "bg-red-500/10 dark:bg-red-900/90 border-red-500/30 text-red-800 dark:text-red-200"
        } animate-in slide-in-from-right duration-200`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="h-9 w-9 rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] hover:bg-[var(--c-sunken-2)] flex items-center justify-center text-[var(--c-ink-3)] hover:text-[var(--c-ink)] transition-all text-sm">
            ←
          </button>
          <div>
            <h1 className="text-3xl text-[var(--c-ink)]" style={DISPLAY}>Soft reserves</h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-[var(--c-ink-3)]">{raidInfo.raidName || raidInfo.name}</span>
              {raidInfo.hostedByGuild && (
                <>
                  <span className="text-[var(--c-ink-4)]">·</span>
                  <span className="text-sm text-[var(--c-ink-3)]">{raidInfo.hostedByGuild}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Status & Controls */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Lock Status */}
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${
            isLocked
              ? "bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300"
              : "bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300"
          }`}>
            <span>{isLocked ? "🔒" : "🔓"}</span>
            <span>{isLocked ? "Locked" : "Open"}</span>
          </div>

          {/* Reserves counter */}
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-xs font-bold text-cyan-700 dark:text-cyan-300">
            {myReserves.length} / {config.max_reserves_per_player} SR
          </div>

          {/* Officer Controls */}
          {canManage && (
            <>
              <button
                onClick={handleToggleLock}
                disabled={actionLoading === "lock"}
                className={`px-3.5 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-1.5 ${
                  isLocked
                    ? "border border-emerald-500/25 bg-emerald-500/10 text-emerald-700/80 dark:text-emerald-300/80 hover:bg-emerald-500/20 hover:text-emerald-800 dark:hover:text-emerald-200 hover:border-emerald-500/40"
                    : "border border-red-500/25 bg-red-500/10 text-red-700/80 dark:text-red-300/80 hover:bg-red-500/20 hover:text-red-800 dark:hover:text-red-200 hover:border-red-500/40"
                }`}
              >
                {isLocked ? (
                  <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>Unlock</>
                ) : (
                  <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>Lock</>
                )}
              </button>
              <button
                onClick={() => setShowConfigPanel(!showConfigPanel)}
                className={`px-3.5 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-1.5 ${
                  showConfigPanel
                    ? "border border-cyan-500/30 bg-cyan-500/15 text-cyan-700 dark:text-cyan-300"
                    : "border border-[var(--c-line-strong)] bg-[var(--c-sunken)] text-[var(--c-ink-3)] hover:text-cyan-700 dark:hover:text-cyan-300 hover:border-cyan-500/30 hover:bg-cyan-500/10"
                }`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                Settings
              </button>
            </>
          )}
        </div>
      </div>

      {/* Config Panel (officer only) */}
      {showConfigPanel && canManage && (
        <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] shadow-[var(--c-shadow)] p-6 space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-widest text-[var(--c-ink-3)]">Reserve Settings</h3>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-[var(--c-ink)]">Max Reserves Per Player</div>
              <div className="text-xs text-[var(--c-ink-3)]">Current: {config.max_reserves_per_player}</div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setEditMaxReserves(Math.max(1, editMaxReserves - 1))} className="h-8 w-8 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)] flex items-center justify-center text-lg font-bold">−</button>
              <span className="w-8 text-center text-lg font-bold text-cyan-700 dark:text-cyan-400">{editMaxReserves}</span>
              <button onClick={() => setEditMaxReserves(Math.min(10, editMaxReserves + 1))} className="h-8 w-8 rounded-lg border border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)] hover:text-[var(--c-ink)] flex items-center justify-center text-lg font-bold">+</button>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-[var(--c-ink)]">Allow Duplicate Items</div>
              <div className="text-xs text-[var(--c-ink-3)]">Multiple players can SR the same item</div>
            </div>
            <button
              onClick={() => setEditAllowDupes(!editAllowDupes)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editAllowDupes ? 'bg-cyan-600' : 'bg-[var(--c-sunken-2)]'}`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${editAllowDupes ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-[var(--c-ink)]">Blind Reserves</div>
              <div className="text-xs text-[var(--c-ink-3)]">Players can only see their own SRs. Officers, raid leaders &amp; loot masters see all.</div>
            </div>
            <button
              onClick={() => setEditBlind(!editBlind)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editBlind ? 'bg-cyan-600' : 'bg-[var(--c-sunken-2)]'}`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${editBlind ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--c-line-soft)]">
            <button onClick={() => setShowConfigPanel(false)} className="px-4 py-2 text-xs font-bold text-[var(--c-ink-3)] hover:text-[var(--c-ink)] transition-colors">Cancel</button>
            <button onClick={handleSaveConfig} disabled={actionLoading === "config"} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-xs font-bold text-white transition-colors">Save</button>
          </div>
        </div>
      )}

      {/* Blind mode banner for regular players */}
      {config?.is_blind && !canManage && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl border border-purple-500/20 bg-purple-500/5">
          <span className="text-sm">🙈</span>
          <span className="text-xs text-purple-700 dark:text-purple-300 font-medium">Blind reserves are enabled — you can only see your own soft reserves.</span>
        </div>
      )}

      {/* My Reserves Summary */}
      {myReserves.length > 0 && (
        <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-bold uppercase tracking-widest text-cyan-700/60 dark:text-cyan-400/60">
              Your Reserves ({myReserves.length}/{config.max_reserves_per_player})
            </div>
            {myCharacters.length > 1 && (
              <select
                value={selectedCharId}
                onChange={(e) => setSelectedCharId(e.target.value)}
                className="bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg px-2 py-1 text-xs text-[var(--c-ink)] appearance-none"
              >
                {myCharacters.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {myReserves.map(sr => (
              <div key={sr.id} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[var(--c-surface)] border border-[var(--c-line)] group">
                <a
                  href={wowheadItemUrl(sr.external_item_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-bold text-purple-700 dark:text-purple-300 hover:text-purple-800 dark:hover:text-purple-200"
                  data-wh-icon-size="small"
                >
                  {sr.item_name}
                </a>
                <span className="text-[10px] text-[var(--c-ink-4)]">{sr.boss_name}</span>
                {!isLocked && (
                  <button
                    onClick={() => handleRemoveReserve(sr.id)}
                    disabled={!!actionLoading}
                    className="ml-1 h-5 w-5 rounded-md bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 flex items-center justify-center text-xs opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Character selector (when no reserves yet) */}
      {myReserves.length === 0 && myCharacters.length > 1 && (
        <div className="flex items-center gap-3">
          <span className="text-xs text-[var(--c-ink-3)]">Reserving as:</span>
          <select
            value={selectedCharId}
            onChange={(e) => setSelectedCharId(e.target.value)}
            className="bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg px-3 py-1.5 text-sm text-[var(--c-ink)] appearance-none"
          >
            {myCharacters.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Hard Reserves Banner (always visible) */}
      {hardReserves.length > 0 && (
        <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-amber-700/60 dark:text-amber-400/60 mb-3">Hard Reserves</div>
          <div className="flex flex-wrap gap-2">
            {hardReserves.map(hr => (
              <div key={hr.id} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[var(--c-surface)] border border-amber-500/20 group">
                <span className="text-amber-700 dark:text-amber-400 text-xs">HR</span>
                <a
                  href={wowheadItemUrl(hr.external_item_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-bold text-purple-700 dark:text-purple-300 hover:text-purple-800 dark:hover:text-purple-200"
                  data-wh-icon-size="small"
                >
                  {hr.item_name}
                </a>
                {hr.note && <span className="text-[10px] text-[var(--c-ink-4)] italic">— {hr.note}</span>}
                {hr.reserved_for_name && <span className="text-[10px] text-[var(--c-ink-3)]">→ {hr.reserved_for_name}</span>}
                {canManage && (
                  <button
                    onClick={() => handleRemoveHardReserve(hr.id)}
                    disabled={!!actionLoading}
                    className="ml-1 h-5 w-5 rounded-md bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 flex items-center justify-center text-xs opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[var(--c-line)] pb-0 overflow-x-auto">
        {(["items", "reserves", ...(canManage ? ["hard", "audit"] as const : [])] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab as any)}
            className={`shrink-0 whitespace-nowrap px-3 sm:px-4 py-2.5 text-xs font-bold uppercase tracking-widest transition-all border-b-2 -mb-px ${
              activeTab === tab
                ? "border-cyan-500 text-cyan-700 dark:text-cyan-300"
                : "border-transparent text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)]"
            }`}
          >
            {tab === "items" ? `Loot Table (${lootTable.length})` : tab === "reserves" ? `Reserves (${softReserves.length})` : tab === "hard" ? `Hard Reserves` : `Audit Log`}
          </button>
        ))}

        {/* Gargul Export - right aligned */}
        <div className="ml-auto shrink-0 pl-2">
          <button
            onClick={handleExportGargul}
            disabled={!!actionLoading}
            className="px-3 sm:px-4 py-2 rounded-lg bg-indigo-500/10 border border-indigo-500/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-500/20 text-sm font-bold transition-colors flex items-center gap-1.5 whitespace-nowrap"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /></svg>
            Export Gargul
          </button>
        </div>
      </div>

      {/* Gargul Export Modal */}
      {gargulString && (
        <div className="rounded-2xl border border-indigo-500/20 bg-indigo-500/5 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-widest text-indigo-600/60 dark:text-indigo-400/60">Gargul Import String</div>
            <button onClick={() => setGargulString(null)} className="text-[var(--c-ink-4)] hover:text-[var(--c-ink)] text-xs">✕</button>
          </div>
          <div className="relative">
            <textarea
              readOnly
              value={gargulString}
              rows={3}
              className="w-full bg-[var(--c-surface)] shadow-[var(--c-shadow-lg)] border border-[var(--c-line)] rounded-lg p-3 text-xs text-[var(--c-ink-2)] font-mono resize-none"
            />
            <button
              onClick={copyGargul}
              className={`absolute top-2 right-2 px-3 py-1 rounded-md text-xs font-bold transition-all ${
                gargulCopied
                  ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30"
                  : "bg-[var(--c-sunken-2)] text-[var(--c-ink-2)] hover:bg-[var(--c-sunken-2)] border border-[var(--c-line)]"
              }`}
            >
              {gargulCopied ? "Copied!" : "Copy"}
            </button>
          </div>
          <div className="text-[10px] text-[var(--c-ink-4)]">Paste this into <span className="font-mono text-[var(--c-ink-3)]">/gl sr</span> in-game to import into Gargul</div>
        </div>
      )}

      {/* ── TAB: Loot Table ─────────────────────────────────── */}
      {activeTab === "items" && (
        <div className="space-y-4">
          {/* Chain Raid Tabs (only shown for chained raids) */}
          {isChainedRaid && (
            <div className="flex gap-1 bg-[var(--c-surface)] border border-[var(--c-line)] p-1 rounded-xl">
              <button
                onClick={() => { setActiveRaidTab(null); setBossFilter(null); }}
                className={`flex-1 py-2 px-3 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${
                  !activeRaidTab ? "bg-amber-500/20 text-amber-800 dark:text-amber-200 border border-amber-500/30" : "text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)]"
                }`}
              >
                All Raids
              </button>
              {chainRaidLabels.map(label => (
                <button
                  key={label}
                  onClick={() => { setActiveRaidTab(label); setBossFilter(null); }}
                  className={`flex-1 py-2 px-3 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${
                    activeRaidTab === label ? "bg-amber-500/20 text-amber-800 dark:text-amber-200 border border-amber-500/30" : "text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          
          {/* Filters */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search items..."
              className="flex-1 bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg px-4 py-2.5 text-sm text-[var(--c-ink)] placeholder:text-[var(--c-ink-4)] focus:border-cyan-500/50 focus:outline-none transition-all"
            />
            <select
              value={bossFilter || ""}
              onChange={(e) => setBossFilter(e.target.value || null)}
              className="bg-[var(--c-surface)] border border-[var(--c-line)] rounded-lg px-3 py-2.5 text-sm text-[var(--c-ink)] appearance-none w-full sm:w-auto sm:min-w-[180px]"
            >
              <option value="">All Bosses</option>
              {bossList.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          {/* Items Grid */}
          <div className="grid gap-2">
            {filteredLoot.map(item => {
              const isHardReserved = hardReservedItemIds.has(item.id);
              const isMyReserve = myReservedItemIds.has(item.id);
              const srCount = softReserves.filter(sr => sr.loot_item_id === item.id).length;
              const classRestrictions: string[] | null = item.metadata?.class_restrictions || null;
              const selectedChar = myCharacters.find(c => c.id === selectedCharId);
              const classEligible = !classRestrictions || classRestrictions.length === 0 || (selectedChar?.class_name && classRestrictions.includes(selectedChar.class_name));
              const canReserve = !isLocked && !isHardReserved && classEligible && myReserves.length < config.max_reserves_per_player && (config.allow_duplicate_items || !isMyReserve);

              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-2.5 sm:gap-4 px-3 sm:px-4 py-3 rounded-xl border transition-all ${
                    isHardReserved
                      ? "border-amber-500/20 bg-amber-500/5 opacity-60"
                      : isMyReserve
                      ? "border-cyan-500/20 bg-cyan-500/5"
                      : "border-[var(--c-line)] bg-[var(--c-surface)] hover:bg-[var(--c-surface)] hover:border-[var(--c-line-strong)]"
                  }`}
                >
                  {/* Item Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <a
                        href={wowheadItemUrl(item.external_item_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-bold text-purple-700 dark:text-purple-300 hover:text-purple-800 dark:hover:text-purple-200 truncate"
                        data-wh-icon-size="small"
                      >
                        {item.name}
                      </a>
                      {isHardReserved && (
                        <span className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-500/30">HR</span>
                      )}
                      {isMyReserve && (
                        <span className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-bold bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 border border-cyan-500/30">YOUR SR</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[11px] text-[var(--c-ink-3)]">{item.boss_name}</span>
                      {/* Show raid label badge in chain mode when viewing all or searching */}
                      {isChainedRaid && item.raid_label && (!activeRaidTab || searchQuery.trim()) && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700/70 dark:text-amber-300/70 border border-amber-500/20">
                          {item.raid_label}
                        </span>
                      )}
                      {classRestrictions && (
                        <div className="flex gap-1">
                          {classRestrictions.map(cls => (
                            <span key={cls} className="text-[9px] font-bold px-1 rounded" style={{ color: tc(CLASS_COLORS[cls] || "#fff"), backgroundColor: `${CLASS_COLORS[cls] || "#888"}15` }}>
                              {cls}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* SR Count with player names */}
                  {srCount > 0 && (
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-[var(--c-sunken)] border border-[var(--c-line-soft)]" title={softReserves.filter(sr => sr.loot_item_id === item.id).map(sr => sr.character_name).join(", ")}>
                      <span className="text-xs font-bold text-[var(--c-ink-2)]">{srCount}</span>
                      <span className="text-[9px] text-[var(--c-ink-4)]">SR</span>
                      <div className="hidden md:flex gap-1 ml-1">
                        {softReserves.filter(sr => sr.loot_item_id === item.id).slice(0, 3).map(sr => (
                          <span key={sr.id} className="text-[9px] font-bold" style={{ color: tc(CLASS_COLORS[sr.class_name || ""] || "#fff") }}>{sr.character_name}</span>
                        ))}
                        {srCount > 3 && <span className="text-[9px] text-[var(--c-ink-4)]">+{srCount - 3}</span>}
                      </div>
                    </div>
                  )}

                  {/* Action */}
                  <div className="shrink-0 flex items-center gap-1">
                    {isMyReserve && (
                      <button
                        onClick={() => {
                          const srs = myReserves.filter(r => r.loot_item_id === item.id);
                          if (srs.length > 0) handleRemoveReserve(srs[srs.length - 1].id);
                        }}
                        disabled={isLocked || !!actionLoading}
                        className="px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300 text-xs font-bold hover:bg-red-500/20 transition-all disabled:opacity-50"
                      >
                        {myReserves.filter(r => r.loot_item_id === item.id).length > 1 
                          ? `Remove (${myReserves.filter(r => r.loot_item_id === item.id).length}x)` 
                          : "Remove"}
                      </button>
                    )}
                    {isHardReserved && !isMyReserve ? (
                      <span className="text-xs text-amber-700/50 dark:text-amber-400/50 font-bold">Hard Reserved</span>
                    ) : !isHardReserved && canReserve ? (
                      <button
                        onClick={() => handleAddReserve(item.id)}
                        disabled={!!actionLoading}
                        className="px-3 py-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-700 dark:text-cyan-300 text-xs font-bold hover:bg-cyan-500/20 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        {actionLoading === `add-${item.id}` ? "..." : "Reserve"}
                      </button>
                    ) : !isHardReserved && !classEligible && !isLocked ? (
                      <span className="text-[10px] text-red-600/60 dark:text-red-400/60 font-bold">Class Restricted</span>
                    ) : null}

                    {/* HR button for officers */}
                    {canManage && !isHardReserved && (
                      <button
                        onClick={() => handleAddHardReserve(item.id)}
                        disabled={!!actionLoading}
                        className="ml-1 px-2 py-1.5 rounded-lg text-amber-700/40 dark:text-amber-400/40 hover:text-amber-700 dark:hover:text-amber-400 hover:bg-amber-500/10 text-[10px] font-bold transition-all"
                        title="Hard Reserve"
                      >
                        HR
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            {filteredLoot.length === 0 && (
              <div className="py-12 text-center text-[var(--c-ink-4)] text-sm">No items match your search</div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB: Reserves Overview (grouped by player) ─────── */}
      {activeTab === "reserves" && (
        <div className="space-y-4">
          {reservesByPlayer.length === 0 ? (
            <div className="py-12 text-center text-[var(--c-ink-4)] text-sm">No soft reserves yet</div>
          ) : (
            reservesByPlayer.map(({ characterId, characterName, className, reserves }) => (
              <div key={characterId} className="rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] p-4">
                <div className="flex items-center gap-3 mb-3">
                  <span
                    className="text-sm font-bold"
                    style={{ color: tc(CLASS_COLORS[className || ""] || "#fff") }}
                  >
                    {characterName}
                  </span>
                  <span className="text-[10px] text-[var(--c-ink-4)]">{className}</span>
                  <span className="ml-auto px-2 py-0.5 rounded-full bg-[var(--c-sunken)] text-xs font-bold text-[var(--c-ink-3)]">{reserves.length} / {config?.max_reserves_per_player} SR</span>
                </div>
                <div className="space-y-1.5">
                {reserves.map(sr => (
                    <div
                      key={sr.id}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--c-sunken)] border border-[var(--c-line-soft)]"
                >
                  <a
                    href={wowheadItemUrl(sr.external_item_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-bold text-purple-700 dark:text-purple-300 hover:text-purple-800 dark:hover:text-purple-200"
                        data-wh-icon-size="small"
                      >
                        {sr.item_name}
                      </a>
                      <span className="text-[10px] text-[var(--c-ink-4)]">{sr.boss_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
      {/* ── TAB: Hard Reserves (officer) ────────────────────── */}
      {activeTab === "hard" && canManage && (
        <div className="space-y-4">
          <div className="text-xs text-[var(--c-ink-3)] mb-2">Hard reserved items cannot be soft reserved by players. Existing soft reserves are automatically removed.</div>
          {hardReserves.length === 0 ? (
            <div className="py-12 text-center text-[var(--c-ink-4)] text-sm">No hard reserves set. Use the "HR" button on items in the Loot Table tab.</div>
          ) : (
            hardReserves.map(hr => (
              <div key={hr.id} className="flex items-center gap-4 px-4 py-3 rounded-xl border border-amber-500/20 bg-amber-500/5">
                <a
                  href={wowheadItemUrl(hr.external_item_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-bold text-purple-700 dark:text-purple-300 hover:text-purple-800 dark:hover:text-purple-200"
                  data-wh-icon-size="small"
                >
                  {hr.item_name}
                </a>
                <span className="text-[11px] text-[var(--c-ink-3)]">{hr.boss_name}</span>
                {hr.reserved_for_name && <span className="text-xs text-amber-700 dark:text-amber-300">→ {hr.reserved_for_name}</span>}
                {hr.note && <span className="text-xs text-[var(--c-ink-4)] italic">{hr.note}</span>}
                <button
                  onClick={() => handleRemoveHardReserve(hr.id)}
                  disabled={!!actionLoading}
                  className="ml-auto px-3 py-1 rounded-lg bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300 text-xs font-bold hover:bg-red-500/20 transition-all"
                >
                  Remove
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── TAB: Audit Log (officer) ────────────────────────── */}
      {activeTab === "audit" && canManage && (
        <div className="space-y-2">
          {auditLog.length === 0 ? (
            <div className="py-12 text-center text-[var(--c-ink-4)] text-sm">No activity yet</div>
          ) : (
            auditLog.map(entry => {
              const actionInfo = ACTION_LABELS[entry.action] || { label: entry.action, color: "text-[var(--c-ink-2)]" };
              return (
                <div key={entry.id} className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-[var(--c-surface)] border border-[var(--c-line-soft)]">
                  <span className={`text-xs font-bold ${actionInfo.color}`}>{actionInfo.label}</span>
                  {entry.character_name && (
                    <span className="text-xs text-[var(--c-ink-2)]">{entry.character_name}</span>
                  )}
                  {entry.item_name && (
                    <>
                      <span className="text-[var(--c-ink-4)]">→</span>
                      {entry.external_item_id ? (
                        <a
                          href={wowheadItemUrl(entry.external_item_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-bold text-purple-700 dark:text-purple-300 hover:text-purple-800 dark:hover:text-purple-200"
                        >
                          {entry.item_name}
                        </a>
                      ) : (
                        <span className="text-xs text-[var(--c-ink-3)]">{entry.item_name}</span>
                      )}
                    </>
                  )}
                  <span className="ml-auto text-[10px] text-[var(--c-ink-4)]">
                    {entry.performed_by_name && <span className="text-[var(--c-ink-4)] mr-2">{entry.performed_by_name}</span>}
                    {new Date(entry.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}
    {/* SR Complete Celebration */}
    {showSrComplete && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] transition-all duration-300 ease-out opacity-100 translate-y-0`}
        >
          <div className="relative overflow-hidden rounded-2xl shadow-2xl border-2 border-purple-400/50 bg-gradient-to-r from-purple-900/95 via-indigo-900/95 to-purple-900/95 backdrop-blur-xl">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-indigo-400/20 to-purple-500/20 animate-pulse" />
            <div className="absolute top-2 left-4 w-2 h-2 bg-yellow-300 rounded-full animate-ping opacity-75" />
            <div className="absolute top-4 right-8 w-1.5 h-1.5 bg-cyan-300 rounded-full animate-ping opacity-75" style={{ animationDelay: '0.5s' }} />
            <div className="absolute bottom-3 left-12 w-1.5 h-1.5 bg-purple-300 rounded-full animate-ping opacity-75" style={{ animationDelay: '1s' }} />
            <div className="relative px-6 py-5 flex items-center gap-5">
              <div className="shrink-0">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center shadow-lg shadow-purple-500/30 animate-bounce" style={{ animationDuration: '2s' }}>
                  <svg className="w-8 h-8 text-[var(--c-ink)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-black text-[var(--c-ink)] flex items-center gap-2">
                  Soft Reserves Complete!
                </h3>
                <p className="text-sm text-purple-800/80 dark:text-purple-100/80 mt-0.5">
                  You've set all {config?.max_reserves_per_player || 2} reserves. You're ready to raid!
                </p>
              </div>
              <button
                onClick={() => setShowSrComplete(false)}
                className="shrink-0 w-8 h-8 rounded-full hover:bg-[var(--c-sunken-2)] flex items-center justify-center transition-colors text-[var(--c-ink-2)] hover:text-[var(--c-ink)]"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="h-1 bg-[var(--c-surface)]">
              <div 
                className="h-full bg-gradient-to-r from-purple-400 to-indigo-400"
                style={{ animation: 'sr-shrink 8s linear forwards' }}
              />
            </div>
          </div>
          <style>{`
            @keyframes sr-shrink {
              from { width: 100%; }
              to { width: 0%; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
